import { DateTime } from 'luxon'

import type { DayPeriod, DateInFullConfig } from './types'

const numberWithLeftZero = (value: number, zeroAmount = 2) =>
  String(value).padStart(zeroAmount, '0')

export const convertArrayToDateTime = (dateArray: Array<number>): DateTime => {
  const [year, month, day, hour, minute, second] = dateArray

  const dateTime = DateTime.local(year, month, day, hour, minute, second)

  return dateTime
}

export const getDayPeriod = (date: DateTime | Array<number>): DayPeriod => {
  if (!date) return
  const dateTime = date instanceof Array ? convertArrayToDateTime(date) : date

  if (dateTime.hour < 12) {
    return 'manhã'
  }

  if (dateTime.hour < 19) {
    return 'tarde'
  }

  return 'noite'
}

export const getDateInFull = (
  date: DateTime | Array<number>,
  { reversed = false, year }: DateInFullConfig,
): string => {
  if (!date) return
  const dateTime = date instanceof Array ? convertArrayToDateTime(date) : date

  const dateInFullConfig: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'long',
    weekday: 'long',
    ...(year && { year }), // se for passado o ano, adiciona o ano
  }

  const dateInFull = dateTime.toLocaleString(dateInFullConfig)

  if (reversed) {
    return dateInFull.split(', ').reverse().join(', ')
  }

  return dateInFull
}

export const getDateAndTimeAsString = (
  date: DateTime | Array<number> | string,
): string => {
  if (!date) return
  let dateTime: DateTime

  if (date instanceof Array) {
    dateTime = convertArrayToDateTime(date)
  } else if (typeof date === 'string') {
    dateTime = convertArrayToDateTime(convertStringIntoDateArray(date))
  } else {
    dateTime = date
  }

  return `${numberWithLeftZero(dateTime?.day)}/${numberWithLeftZero(
    dateTime?.month,
  )}/${numberWithLeftZero(dateTime?.year)}, às ${numberWithLeftZero(
    dateTime?.hour,
  )}:${numberWithLeftZero(dateTime?.minute)}`
}

export const getTimeInHourAndMinute = (
  date: DateTime | Array<number>,
): string => {
  const dateTime = date instanceof Array ? convertArrayToDateTime(date) : date

  const time = dateTime.toLocaleString({
    hour: '2-digit',
    minute: '2-digit',
  })

  return time
}

export const convertStringIntoDateArray = (value: string): Array<number> => {
  if (!value) return []

  const [date, time] = value?.split(' ')
  if (!date || !time) {
    return []
  }

  const [day, month, year] = date?.split('/')
  const [hour, minutes, seconds] = time.split(':')

  const orderedDateTime = [year, month, day, hour, minutes, seconds]
  const transformIntoZeroUndefinedValues = orderedDateTime?.map(i => i || '0')
  const transformItemsIntoNumbers = transformIntoZeroUndefinedValues?.map(i =>
    parseInt(i),
  )
  return transformItemsIntoNumbers
}

export const convertStringIntoDateTime = (value: string): DateTime => {
  const dateArray = convertStringIntoDateArray(value)
  return convertArrayToDateTime(dateArray)
}

export const getUnixTimestamp = (date: Date): number => {
  return Math.floor(date?.getTime() / 1000)
}

export const formatToBRDate = (date): string => {
  if (typeof date === 'object') {
    const dt = DateTime.fromJSDate(date)
    return dt.toFormat('dd/MM/yyyy')
  } else {
    return date
  }
}

export const isoToBrazilianDate = (date: string): string => {
  const formattedDate = DateTime.fromISO(date)
  if (!formattedDate.isValid) {
    return date
  }
  return formattedDate.toFormat('dd/MM/yyyy')
}

export const dateTimeNowBR = (): string => {
  return new Date()
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    ?.replace(',', '')
}
